import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Box, Button, Flex, Text, Textarea } from '@chakra-ui/react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateInterpretationText, getSettingsForInterpretation } from '../../redux/reducers';
import { SettingsIcon } from '@chakra-ui/icons';
import {
  useLocalDataTrack,
  useDataTrackMessages,
  useNumberOfRemoteDataTrack,
} from '../../twilio/hooks';
import { UserIcon } from '../../images';
import { openModal, Modals } from '../../redux/reducers';

const showWriterMaxTime = 12000; // 12 seconds

const textTrackFilter = (track) => track.name === 'text';

const TextSession = ({ ...containerProps }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { color, bgColor, altBg, font, fontSize } = useSelector(
    getSettingsForInterpretation,
    shallowEqual
  );
  const { text, lastWriter, lastChangeDate } = useSelector((state) => state.activeSession.text);
  const { roles, members } = useSelector((state) => state.activeSession);
  const self = members.find((m) => m.isSelf);
  const ownRole = self && roles[self.name];
  const isInterpreter = ownRole === 'interpreter';
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [needSync, setNeedSync] = useState(true);
  const [textTooLong, setTextTooLong] = useState(false);

  const ownTextTrack = useLocalDataTrack(textTrackFilter);
  const numberOfRemoteTextTracks = useNumberOfRemoteDataTrack(textTrackFilter);

  useEffect(() => {
    if (!needSync) {
      return;
    }
    if (!ownTextTrack || numberOfRemoteTextTracks === 0) {
      // wait until data tracks are connected
      return;
    }
    ownTextTrack.send(JSON.stringify({ request: true }));
    setNeedSync(false);
  }, [needSync, ownTextTrack, numberOfRemoteTextTracks]);

  const handleReceivedMessage = useCallback(
    (message, track, participant) => {
      try {
        const obj = JSON.parse(message);
        if (obj.request) {
          if (ownTextTrack) {
            dispatch((dispatch, getState) => {
              const {
                text: { text: currentText, version: currentVersion },
              } = getState().activeSession;
              if (!currentVersion) {
                return;
              }
              ownTextTrack.send(
                JSON.stringify({ text: currentText, version: currentVersion, sync: true })
              );
            });
          } else {
            console.warn('no text data track');
          }
          return;
        }
        const { text, version, sync } = obj;
        dispatch(updateInterpretationText(sync ? null : participant.identity, text, version));
      } catch (error) {
        console.warn('Failed to handle received message', error);
      }
    },
    [dispatch, ownTextTrack]
  );

  const now = +new Date();
  const intervalSinceLastUpdate = lastChangeDate && now - lastChangeDate;
  const lastWriterIsSelf = self && lastWriter === self.name;
  useEffect(() => {
    const hideWriterAfter = showWriterMaxTime - intervalSinceLastUpdate;
    if (hideWriterAfter > 0 && !lastWriterIsSelf) {
      const timeout = setTimeout(forceUpdate, hideWriterAfter);
      return () => clearTimeout(timeout);
    }
  }, [lastChangeDate]);

  useEffect(() => {
    if (textTooLong) {
      const timeout = setTimeout(() => setTextTooLong(false), 1000);
      return () => clearTimeout(timeout);
    }
  }, [textTooLong]);

  useDataTrackMessages(textTrackFilter, handleReceivedMessage);

  const handleTextChange = useCallback(
    (e) => {
      let text = e.target.value;
      const MAX_TEXT_LENGTH = 2000;
      if (text.length > MAX_TEXT_LENGTH) {
        text = text.substr(-MAX_TEXT_LENGTH);
        setTextTooLong(true);
      }

      if (ownTextTrack) {
        dispatch((dispatch, getState) => {
          const {
            text: { version: currentVersion },
          } = getState().activeSession;
          const version = currentVersion + 1;
          ownTextTrack.send(JSON.stringify({ text, version }));
          dispatch(updateInterpretationText(self.name, text, version));
        });
      } else {
        console.warn('no text data track');
      }
    },
    [dispatch, ownTextTrack]
  );

  const barHeight = '40px';

  const textareaRef = useRef();

  const [autoScroll, setAutoScroll] = useState(true);
  const startTracking = useCallback(() => setAutoScroll(true), [textareaRef]);
  const disableNextScrollEvent = useRef(false);
  const onScroll = useCallback(() => {
    if (disableNextScrollEvent.current) {
      disableNextScrollEvent.current = false;
    } else {
      setAutoScroll(false);
    }
  });

  useEffect(() => {
    if (!textareaRef.current) return;
    if ((!self || lastWriter === self.name) && intervalSinceLastUpdate < showWriterMaxTime) return;
    if (autoScroll) {
      if (textareaRef.current.scrollTop != textareaRef.current.scrollHeight) {
        disableNextScrollEvent.current = true;
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      }
    }
  });

  const openSettings = useCallback(() => {
    dispatch(openModal({ mode: 'interpretation' }, Modals.SETTINGS));
  }, [dispatch]);

  return (
    <Box {...containerProps} sx={{ position: 'relative' }}>
      <Box height={barHeight} bg={textTooLong ? 'brand.50' : altBg}>
        <Box sx={{ float: 'right', color }}>
          <Button bg="null" m={0} p={0} onClick={openSettings}>
            <SettingsIcon w={6} h={6} mr={1} />
          </Button>
        </Box>
        {intervalSinceLastUpdate < showWriterMaxTime && lastWriter && !lastWriterIsSelf && (
          <Flex color={color} height="100%" alignItems="center" mx={4}>
            <UserIcon />
            <Text ml={2}>
              <Text as="span" fontWeight="500">
                {lastWriter}
              </Text>{' '}
              {t('chat.isWriting')}
            </Text>
          </Flex>
        )}
      </Box>
      <Textarea
        sx={{
          color,
          bg: bgColor,
          fontFamily: font,
          fontSize,
          borderRadius: 0,
          border: 'none',
          width: '100%',
          height: `calc(100% - ${barHeight})`,
        }}
        ref={textareaRef}
        readOnly={!isInterpreter}
        value={text}
        onChange={handleTextChange}
        placeholder="Here is a sample placeholder"
        resize="none"
        onScroll={onScroll}
      />
      {autoScroll === false && (
        <Button
          sx={{
            zIndex: 1,
            position: 'absolute',
            bottom: 2,
            right: 2,
            bg: color,
            color: bgColor,
          }}
          onClick={startTracking}
        >
          {t('chat.track')}
        </Button>
      )}
    </Box>
  );
};

TextSession.propTypes = {};

export default TextSession;
